import api from './api'

/**
 * Serviço Microsoft Graph API
 * 
 * Wraps os endpoints do backend que fazem proxy para a Microsoft Graph API.
 * Requer integração Microsoft conectada (OAuth) para o tenant.
 */

function getTenantId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return user.tenantId || localStorage.getItem('tenantId') || ''
  } catch {
    return localStorage.getItem('tenantId') || ''
  }
}

const basePath = () => `/api/tenants/${getTenantId()}/integrations/microsoft`

export default {

  // ===== Status =====

  /**
   * Verificar se Microsoft está conectada para o tenant
   * GET /api/integrations/microsoft/status
   */
  getStatus() {
    return api.get('/api/integrations/microsoft/status')
  },

  // ===== Online Meetings (Teams) =====

  /**
   * Criar reunião online Teams
   * POST /api/tenants/{tenantId}/integrations/microsoft/meetings
   * @param {Object} data - { subject, startDateTime, endDateTime, timeZone }
   * @returns {{ id, subject, joinWebUrl, startDateTime, endDateTime, organizer }}
   */
  createMeeting(data) {
    return api.post(`${basePath()}/meetings`, data)
  },

  /**
   * Listar reuniões futuras
   * GET /api/tenants/{tenantId}/integrations/microsoft/meetings
   */
  listMeetings() {
    return api.get(`${basePath()}/meetings`)
  },

  /**
   * Atualizar reunião
   * PATCH /api/tenants/{tenantId}/integrations/microsoft/meetings/{meetingId}
   */
  updateMeeting(meetingId, data) {
    return api.patch(`${basePath()}/meetings/${meetingId}`, data)
  },

  /**
   * Remover reunião
   * DELETE /api/tenants/{tenantId}/integrations/microsoft/meetings/{meetingId}
   */
  deleteMeeting(meetingId) {
    return api.delete(`${basePath()}/meetings/${meetingId}`)
  },

  // ===== Calendar Events (Outlook) =====

  /**
   * Criar evento no calendário Outlook
   * POST /api/tenants/{tenantId}/integrations/microsoft/calendar/events
   * @param {Object} data - { subject, startDateTime, endDateTime, timeZone, attendees, isOnlineMeeting }
   * @returns {{ id, subject, startDateTime, endDateTime, onlineMeetingUrl, webLink, attendees, organizer }}
   */
  createCalendarEvent(data) {
    return api.post(`${basePath()}/calendar/events`, data)
  },

  /**
   * Listar eventos do calendário
   * GET /api/tenants/{tenantId}/integrations/microsoft/calendar/events
   * @param {Object} params - { startDateTime, endDateTime, top }
   */
  listCalendarEvents(params = {}) {
    return api.get(`${basePath()}/calendar/events`, { params })
  },

  /**
   * Atualizar evento do calendário
   * PATCH /api/tenants/{tenantId}/integrations/microsoft/calendar/events/{eventId}
   */
  updateCalendarEvent(eventId, data) {
    return api.patch(`${basePath()}/calendar/events/${eventId}`, data)
  },

  /**
   * Remover evento do calendário
   * DELETE /api/tenants/{tenantId}/integrations/microsoft/calendar/events/{eventId}
   */
  deleteCalendarEvent(eventId) {
    return api.delete(`${basePath()}/calendar/events/${eventId}`)
  },

  /**
   * Verificar disponibilidade de participantes
   * POST /api/tenants/{tenantId}/integrations/microsoft/calendar/availability
   * @param {Object} data - { emails, startDateTime, endDateTime, timeZone }
   */
  checkAvailability(data) {
    return api.post(`${basePath()}/calendar/availability`, data)
  },

  // ===== Mail (Outlook) =====

  /**
   * Enviar e-mail via Outlook
   * POST /api/tenants/{tenantId}/integrations/microsoft/mail/send
   * @param {Object} data - { subject, bodyContent, contentType, toRecipients }
   */
  sendMail(data) {
    return api.post(`${basePath()}/mail/send`, data)
  },

  // ===== Teams Messages =====

  /**
   * Enviar mensagem em canal Teams
   * POST /api/tenants/{tenantId}/integrations/microsoft/teams/{teamId}/channels/{channelId}/messages
   */
  sendChannelMessage(teamId, channelId, data) {
    return api.post(`${basePath()}/teams/${teamId}/channels/${channelId}/messages`, data)
  },

  /**
   * Listar mensagens de canal
   * GET /api/tenants/{tenantId}/integrations/microsoft/teams/{teamId}/channels/{channelId}/messages
   */
  listChannelMessages(teamId, channelId, params = {}) {
    return api.get(`${basePath()}/teams/${teamId}/channels/${channelId}/messages`, { params })
  },

  /**
   * Responder em thread de mensagem
   * POST /api/tenants/{tenantId}/integrations/microsoft/teams/{teamId}/channels/{channelId}/messages/{messageId}/replies
   */
  replyToChannelMessage(teamId, channelId, messageId, data) {
    return api.post(`${basePath()}/teams/${teamId}/channels/${channelId}/messages/${messageId}/replies`, data)
  },

  /**
   * Criar chat 1:1
   * POST /api/tenants/{tenantId}/integrations/microsoft/chats
   */
  createChat(data) {
    return api.post(`${basePath()}/chats`, data)
  },

  /**
   * Enviar mensagem em chat
   * POST /api/tenants/{tenantId}/integrations/microsoft/chats/{chatId}/messages
   */
  sendChatMessage(chatId, data) {
    return api.post(`${basePath()}/chats/${chatId}/messages`, data)
  },

  /**
   * Listar mensagens de chat
   * GET /api/tenants/{tenantId}/integrations/microsoft/chats/{chatId}/messages
   */
  listChatMessages(chatId, params = {}) {
    return api.get(`${basePath()}/chats/${chatId}/messages`, { params })
  },
}
